import _ from 'underscore';
import AnalyticsUtil from 'utils/AnalyticsUtil';
import Backbone from 'backbone';
import Localizer from 'localizer';
import Loadable from 'utils/extensions/Loadable';
import DevicesPopupMenuItemView from 'views/navigation/DevicesPopupMenuItemView';
import SensorsPopupMenuItemView from 'views/navigation/SensorsPopupMenuItemView';
import ModalConfirmationView from 'views/main/Modal/ModalConfirmationView';
import ActiveActivityTrackerView from 'views/device/ActiveActivityTrackerView';
import ModalActiveActivityTrackerView from 'views/device/ModalActiveActivityTrackerView';
import QueueCount from 'models/device/QueueCount';
import WellnessDeviceInfo from 'models/device/WellnessDeviceInfo';
import Devices from 'collections/device/Devices';
import SensorDevices from 'collections/device/SensorDevices';
import QueueNotifications from 'collections/device/QueueNotifications';
import ActivityTrackerDispatcher from 'events/ActivityTrackerDispatcher';
import NavigationUtil from 'utils/NavigationUtil';
import ActiveIndicatorEnum from 'utils/device/ActiveIndicatorEnum';
import DeviceLinksUtil from 'utils/device/DeviceLinksUtil';
import QueuePoller from 'utils/QueuePoller';
import PrimaryActivityTracker from 'clients/device/PrimaryActivityTracker';
import UserDevice from 'models/device/UserDevice';
import ReportTypeMetrics from 'utils/ReportTypeMetrics';
import templateHtml from 'templates/main/MainContainer/DevicesPopupMenuView.html';
import UserPreference from 'models/user/UserPreference';
import Preferences from 'models/preferences/Preferences';
import PrimaryTrainingDevice from '../../../models/device/PrimaryTrainingDevice';
import { enableAppStoreWebLinkRoleKey } from '../../../models/preferences/PreferencesMapping';

var Events = {
    ON_NAVIGATION: 'ON_NAVIGATION',
    REFRESH_COUNT: 'REFRESH_COUNT',
    SET_DEVICES_SYNC_DATA: 'SET_DEVICES_SYNC_DATA'
};

var LoadableView = Backbone.View.extend(Loadable);

var DevicesPopupMenuView = LoadableView.extend({
    propertyFiles: [
        '/connections/connections',
        '/power_zone/power_zone',
        '/send_to_device/send_to_device',
        '/device/device',
        '/segment/segment',
        '/sensors/sensors',
        '/common/common',
        '/reportswidget/reportswidget',
        '/activity-api/activity-api',
        '/notification/notification'
    ],

    template: _.template(templateHtml),

    initialize: function(args) {
        this.displayName = args.displayName;
        this.userPreferences = args.userPreferences;
        this.pollTimeoutValue = parseInt(args.pollTimeoutValue, 10);
        this.queuePoller = null;
        this.messageAndDevicesNotificationsEnabled = args.messageAndDevicesNotificationsEnabled;
        this.sensorDevicesAvailable = new SensorDevices();
        this.userHasSensors = new UserPreference({
            key: 'Activity.userHasSensors'
        });
        this.devices = new Devices({
            displayName: this.displayName
        });
        /* This model is a double call. This should be removed when DevicesPopupMenuView is refactored to use react and redux. */
        this.primaryTrainingDevice = new PrimaryTrainingDevice();
        this.messages = new QueueNotifications();
        this.listenTo(this.messages, 'error', this.showErrorMessage());

        ActivityTrackerDispatcher.on(
            ActivityTrackerDispatcher.Events.ON_ACTIVE_TRACKER,
            this.fetchUpdatedDevices,
            this
        );

        //if messageAndDevicesNotificationsEnabled is true then it should allow device notification count polling
        if (this.messageAndDevicesNotificationsEnabled) {
            this.queueCount = new QueueCount();
            this.queueCount.bind('add', this.handleQueueCount, this);

            this.configurePolling();
        }

        this.batteryInfos = {};
        this.userDeviceInfos = {};

        this.enableAppStoreWebLink = new Preferences({ key: enableAppStoreWebLinkRoleKey }); //this won't be removed
    },

    configurePolling: function() {
        if (!this.queuePoller) {
            this.queuePoller = new QueuePoller({ pollTime: this.pollTimeoutValue });
        }

        if (this.queuePoller.isPollingStarted() === false) {
            this.queuePoller.addCollection(this.queueCount);
            this.queuePoller.start();
        }
    },

    provideDependencies: function(modelSynchronizer, tripCount) {
        if (tripCount === 0) {
            modelSynchronizer.addModel({ model: this.devices, required: true });
            modelSynchronizer.addModel({ model: this.primaryTrainingDevice, required: true });
            modelSynchronizer.addModel({ model: this.messages, required: true });
            modelSynchronizer.addModel({ model: this.userHasSensors, required: true });
            if (this.queueCount) {
                modelSynchronizer.addModel({ model: this.queueCount, required: true });
            }
            modelSynchronizer.addModel({
                model: this.enableAppStoreWebLink,
                required: true
            });
        } else {
            if (this.userHasSensors && this.userHasSensors.get('value')) {
                modelSynchronizer.addModel({ model: this.sensorDevicesAvailable, required: true });
            }
            _.each(
                this.devices.models,
                function(device) {
                    var deviceId = device.get('deviceId');

                    if (device.get('wellness')) {
                        var wellnessDeviceInfo = new WellnessDeviceInfo({ deviceId: deviceId });
                        modelSynchronizer.addModel({
                            model: wellnessDeviceInfo,
                            required: true
                        });
                        this.batteryInfos[deviceId] = wellnessDeviceInfo;
                    }

                    var userDevice = new UserDevice({ deviceId: deviceId });
                    modelSynchronizer.addModel({ model: userDevice, required: true });
                    this.userDeviceInfos[deviceId] = userDevice;
                }.bind(this)
            );
        }
    },

    onDependenciesLoaded: function(tripCount) {
        if (tripCount === 0) {
            let pwd = this.primaryTrainingDevice.getPrimaryWearableDeviceInfo();
            if (pwd) {
                AnalyticsUtil.push({
                    event: 'track_event',
                    category: 'Primary Wearable Device',
                    action: pwd.applicationKey,
                    label: pwd.applicationKey
                });
            }

            let ptd = this.primaryTrainingDevice.getFullDeviceInfo(
                this.primaryTrainingDevice.getPrimaryTrainingDevice()
            );
            if (ptd) {
                AnalyticsUtil.push({
                    event: 'track_event',
                    category: 'Primary Training Device',
                    action: ptd.applicationKey,
                    label: ptd.applicationKey
                });
            }
            return false;
        } else {
            this.hideErrorMessage();
            this.getBatteryStatusForDevices();
            this.getLastUploadTimestampForDevices();
            return true;
        }
    },

    onDependenciesLoadFailed: function() {
        this.showErrorMessage();
    },

    getLastUploadTimestampForDevices: function() {
        this.devices.each(
            function(device) {
                var userDevice = this.userDeviceInfos[device.get('deviceId')];
                device.set('lastUploadTimestamp', userDevice.get('lastUploadTimestamp'));
            }.bind(this)
        );

        // avoiding extra call to send last upload data for device sync banner
        this.trigger(Events.SET_DEVICES_SYNC_DATA, this.devices.toJSON());

        this.render();
    },

    getBatteryStatusForDevices: function() {
        var batteryStatuses = _.filter(this.batteryInfos, function(batteryInfo) {
            return batteryInfo.get('batteryLow') === true;
        });

        _.each(
            batteryStatuses,
            function(batteryStatus) {
                var device = this.devices.get(batteryStatus.get('deviceId'));
                device.set('batteryLow', true);
            }.bind(this)
        );

        this.sortDevices();
    },

    sortDevices: function() {
        this.devices.sort();

        var retiredDevices = this.devices.where({
                activeInd: ActiveIndicatorEnum.NOT_ACTIVE
            }),
            batteryLowDevices = this.devices.where({ batteryLow: true }),
            regularDevices = _.reject(this.devices.models, function(device) {
                return device.get('activeInd') === ActiveIndicatorEnum.NOT_ACTIVE || device.get('batteryLow') === true;
            });

        _.each(retiredDevices, function(device) {
            regularDevices.push(device);
        });

        _.each(batteryLowDevices, function(device) {
            regularDevices.unshift(device);
        });

        this.devices.reset(regularDevices, { sort: false });
    },

    render: function() {
        var userHasDevices = this.devices.length > 0;
        this.$el.html(
            this.template({
                Localizer: Localizer,
                url: NavigationUtil.url,
                userHasDevices: userHasDevices,
                showManageDevicePriority: this.primaryTrainingDevice.getShowDevicePriority()
            })
        );

        if (!userHasDevices) {
            var fullLocale = this.userPreferences.getFullLocale();
            this.$('#express-link').attr('href', '/' + fullLocale + '/start');
            this.$('#mobile-link').attr(
                'href',
                'https://buy.garmin.com/en-US/US/on-the-go/apps/garmin-connect-mobile/prod125677.html'
            );
        }

        this.renderDevices();
        this.renderSensorDevices();
    },

    renderDevices: function() {
        var messages = this.groupByDeviceId(),
            messagesObject = this.prepareForOutput(messages);

        var trackerCount = this.getTrackerCount();
        let currentPrimaryTrainingDevice = this.primaryTrainingDevice.getPrimaryTrainingDevice();
        let currentPrimaryWearable = this.primaryTrainingDevice.getPrimaryWearableDevice();
        let nonPrimaryDevices = [];

        _.each(
            this.devices.models,
            function(device) {
                var deviceView = new DevicesPopupMenuItemView({
                    device: device,
                    messages: messagesObject[device.get('deviceId')],
                    trackerCount: trackerCount,
                    isRetired: device.get('activeInd') === ActiveIndicatorEnum.NOT_ACTIVE,
                    isPrimaryTrainingDevice: device.get('deviceId') === currentPrimaryTrainingDevice,
                    isPrimaryWearableDevice: device.get('deviceId') === currentPrimaryWearable,
                    enableAppStoreWebLink: this.enableAppStoreWebLink.featureEnabled()
                });

                this.listenTo(
                    deviceView,
                    DevicesPopupMenuItemView.Events.ON_ACTIVITY_TRACKER_CHANGE,
                    this.onActivityTrackerChange
                );
                this.listenTo(
                    deviceView,
                    DevicesPopupMenuItemView.Events.ON_SYNC_MESSAGE_REMOVE,
                    this.onMessageRemove.bind(this, deviceView)
                );
                this.listenTo(deviceView, DevicesPopupMenuItemView.Events.ON_NAVIGATION, function() {
                    this.trigger(Events.ON_NAVIGATION);
                });

                if (
                    device.get('deviceId') === currentPrimaryTrainingDevice ||
                    device.get('deviceId') === currentPrimaryWearable
                ) {
                    this.$el.append(deviceView.render().el);
                } else {
                    nonPrimaryDevices.push(deviceView.render().el);
                }
            },
            this
        );
        nonPrimaryDevices.forEach((element) => {
            this.$el.append(element);
        });
    },
    renderSensorDevices: function() {
        if (this.userHasSensors && this.userHasSensors.get('value') && this.sensorDevicesAvailable) {
            var sensorInfoText = `<div class='header-nav-menu-section' style="font-size: 12px;"><p class="bottom-xs" style="text-transform: uppercase;">${Localizer.localize(
                'sensors'
            )}</p>${Localizer.localize('last_90_days_sensors')}</div>`;
            var listOfSensors = this.sensorDevicesAvailable.toJSON();
            listOfSensors = listOfSensors.sensors;

            this.$el.append(sensorInfoText);

            const currentRegisteredDevices = this.primaryTrainingDevice.getRegisteredDevices();

            _.each(
                listOfSensors,
                function(sensor) {
                    var sensorView = new SensorsPopupMenuItemView({
                        sensor: sensor,
                        userPreferences: this.userPreferences,
                        currentRegisteredDevices
                    });

                    this.$el.append(sensorView.render().el);
                },
                this
            );
        }
    },

    onActivityTrackerChange: function(device) {
        if (device.isInUseByAnotherUser()) {
            this.displayActivityTrackerHelpMessage();
        } else {
            this.displaySetActivityTrackerConfirmation(device);
        }
    },

    onMessageRemove: function(deviceView, messageId, groupName) {
        var id = _.isNumber(messageId) ? messageId : parseInt(messageId.replace(/[^0-9\.]/g, ''), 10), // replace any non digit character with nothing ('') and parse int out of what remains
            message = this.messages.get(id),
            groupRemove = false;

        // if groupName is not null or empty then must be wanting to remove the entire group?
        if (groupName) {
            groupRemove = true;
        }

        if (message) {
            if (groupRemove) {
                this.messages.removeGroup(message.get('deviceId'), message.get('groupName'), {
                    error: function() {
                        this.showErrorMessage();
                        deviceView.hideConfirmationPopup();
                    }.bind(this),
                    success: function() {
                        this.handleRemoveSuccess(deviceView, messageId);
                    }.bind(this)
                });
            } else {
                message.destroy({
                    success: function(response) {
                        this.handleRemoveSuccess(deviceView, messageId, response);
                    }.bind(this),
                    error: function(message) {
                        if (!this.messages.get(message.get('messageId'))) {
                            this.messages.add(message); // destroying a model removes from the collection, even if not successful
                        }

                        this.showErrorMessage();
                        deviceView.hideConfirmationPopup();
                    }.bind(this)
                });
            }
        }
    },

    handleRemoveSuccess: function(deviceView, messageId, message) {
        if (message) {
            //single remove
            var groupName = message.get('groupName'),
                removeGroup = groupName ? this.messages.where({ groupName: groupName }).length === 0 : false;

            deviceView.messages = _.reject(deviceView.messages, {
                messageId: messageId
            });
            deviceView.hideMessageItem(messageId, removeGroup, deviceView.updateMessagesCount.bind(deviceView));
        } else {
            // group remove
            var messages = this.groupByDeviceId(),
                messagesObject = this.prepareForOutput(messages);

            deviceView.messages = messagesObject[deviceView.device.get('deviceId')] || [];
            deviceView.hideMessageItem(messageId, false, deviceView.updateMessagesCount.bind(deviceView));
        }

        if (this.queueCount) {
            this.queueCount.fetch();
        }
        deviceView.hideConfirmationPopup();
        // deviceView.updateMessagesCount();
    },

    displayActivityTrackerHelpMessage: function() {
        var activityTrackerHelpModal = new ModalConfirmationView({
            modalTitle: Localizer.localize('device.set.activity.tracker.MAT').toUpperCase(),
            message: Localizer.localize('set.up.activity.tracker.message.MAT'),
            confirmationTextKey: 'ok',
            showCancelButton: false,
            stopClickPropagationOnModalClick: true,
            onSaveButtonClicked: function(e) {
                return true;
            }
        });
        activityTrackerHelpModal.render();
    },

    displaySetActivityTrackerConfirmation: function(device) {
        var activeTrackerView = new ActiveActivityTrackerView({});

        var modalView = new ModalActiveActivityTrackerView({
            view: activeTrackerView,
            showHeader: true,
            modalTitle: Localizer.localize('device.set.activity.tracker.MAT'),
            stopClickPropagationOnModalClick: true
        });

        this.listenTo(
            modalView,
            ModalActiveActivityTrackerView.Events.ON_SET,
            this.onSetActiveButtonClicked.bind(this, device)
        );
        modalView.render();
    },

    onSetActiveButtonClicked: function(device) {
        if (device) {
            PrimaryActivityTracker.setPrimaryActivityTracker(
                device.get('deviceId'),
                'true',
                this.preferredTrackerUpdateSuccess.bind(this),
                this.preferredTrackerUpdateFail.bind(this)
            );
        }
    },

    preferredTrackerUpdateSuccess: function() {
        this.hideErrorMessage();
        this.load();
    },

    preferredTrackerUpdateFail: function() {
        this.showErrorMessage();
    },

    getTrackerCount: function() {
        var trackerCount = 0;

        _.each(
            this.devices.models,
            function(device) {
                if (device.get('wellness') || device.get('hybrid')) {
                    if (
                        device.get('activeInd') === ActiveIndicatorEnum.ACTIVE ||
                        device.get('activeInd') === ActiveIndicatorEnum.ACTIVE_NOT_TRACKING
                    ) {
                        trackerCount++;
                    }
                }
            },
            this
        );

        return trackerCount;
    },

    // Message Queues
    groupByDeviceId: function() {
        var groupedMessages = _.sortBy(this.messages.toJSON(), function(message) {
            return message.priority;
        });
        groupedMessages = this.groupByMulti(groupedMessages, ['deviceId', 'groupName']);
        return _.toArray(groupedMessages);
    },

    groupByMulti: function(obj, values) {
        if (!values.length) {
            return obj;
        }

        var byFirst = _.groupBy(obj, values[0]);
        var rest = values.slice(1);
        for (var prop in byFirst) {
            byFirst[prop] = this.groupByMulti(byFirst[prop], rest);
        }

        return byFirst;
    },

    prepareForOutput: function(groupByCollection) {
        var queueNotifications = {};

        for (var i = 0; i < groupByCollection.length; i++) {
            var displaySendButton = true;
            var propertyArray = [];
            for (var groupName in groupByCollection[i]) {
                propertyArray.push(groupName);
            }
            // Need to sort so that the software updates will be at the bottom of the widget
            // lowest priority
            propertyArray.sort();
            var queueArray = [],
                deviceId = null;
            for (var n = 0; n < propertyArray.length; n++) {
                var notifications = groupByCollection[i][propertyArray[n]];
                notifications = _.sortBy(notifications, function(queue) {
                    return queue.priority;
                });
                for (var x = 0; x < notifications.length; x++) {
                    if (!notifications[x].hidden) {
                        var queue = notifications[x],
                            hasGroupName =
                                queue.groupName !== 'null' && queue.groupName !== null && queue.groupName !== undefined,
                            isGroupHeading = hasGroupName && x == 0,
                            isGroupChild = hasGroupName && x > 0,
                            groupNameClass = hasGroupName ? queue.groupName.split(' ').join('_') + queue.deviceId : '',
                            showDeleteButton = true;

                        if (!deviceId) {
                            deviceId = queue.deviceId;
                        }

                        if (isGroupHeading) {
                            var groupHeader = {
                                deviceName: queue.deviceName,
                                applicationKey: queue.applicationKey,
                                deviceId: queue.deviceId,
                                messageId: '__' + queue.messageId,
                                wifiSetup: queue.wifiSetup,
                                groupName: queue.groupName,
                                hasGroupName: true,
                                isGroupHeading: true,
                                isGroupChild: false,
                                groupNameClass: groupNameClass,
                                displaySendButton: false,
                                messageType: queue.messageType,
                                showDeleteButton: showDeleteButton,
                                metaData: queue.metaData,
                                groupHeadingText: '',
                                showMessageType: true
                            };
                            this.addMessageTypeToQueue(groupHeader);
                            if (queue.messageType === 'software-update') {
                                groupHeader.groupName = Localizer.localize('queue.software.header');
                            } else if (queue.groupName && queue.groupName.toLowerCase().indexOf('strava-') > -1) {
                                groupHeader.groupHeadingText =
                                    '<span class="sync-type">' +
                                    Localizer.localize(
                                        queue.groupName.toLowerCase().indexOf('running') > -1 ? 'running' : 'cycling'
                                    ) +
                                    '</span>';
                                groupHeader.groupName = Localizer.localize('strava_live_segments', '');
                            } else if (queue.groupName && queue.groupName.toUpperCase() === 'CALENDAR_SYNC') {
                                groupHeader.groupHeadingText = Localizer.localize('queue_calendar_sync_info');
                                groupHeader.groupName = Localizer.localize('queue_calendar');
                                groupHeader.showDeleteButton = false;
                                groupHeader.showMessageType = false;
                                groupHeader.connectUrl = NavigationUtil.url('/calendar');
                            } else if (queue.messageType === 'schedules') {
                                groupHeader.displayMessageType = Localizer.localize('queue.training.plan');
                            } else if (queue.messageType === 'pace-band') {
                                groupHeader.groupName = Localizer.localize('queue.pacepro');
                                groupHeader.connectUrl = NavigationUtil.url('/pacepro');
                            }

                            isGroupHeading = false;
                            isGroupChild = true;

                            queueArray.push(groupHeader);
                        }

                        if (
                            queue.messageType === 'segment-leaders' ||
                            queue.messageType === 'segment-leaders-running'
                        ) {
                            showDeleteButton = false;
                        }

                        if (queue.messageType === 'software-update') {
                            queue.useSyncUpdateClass = queue.softwareUpdateCount === 1;
                        } else {
                            queue.useSyncUpdateClass = false;
                        }

                        queue.showDeleteButton = showDeleteButton;
                        queue.hasGroupName = hasGroupName;
                        queue.isGroupHeading = isGroupHeading;
                        queue.isGroupChild = isGroupChild;
                        queue.groupNameClass = groupNameClass;
                        queue.displaySendButton = displaySendButton;
                        queue.appSupport = this.supportApps(queue);

                        this.addMessageTypeToQueue(queue);

                        if (queue.appSupport === true) {
                            queue.appStoreUrl = DeviceLinksUtil.getAppStoreUrl(this.getDevice(queue));
                        }

                        queueArray.push(queue);
                    }
                }
            }
            queueNotifications[deviceId] = queueArray;
        }
        return queueNotifications;
    },

    supportApps: function(queue) {
        var supports = false;
        var device = this.getDevice(queue);
        if (device != null) {
            supports = device.get('appSupport');
        }
        return supports;
    },

    getDevice: function(queue) {
        var device = null;
        for (var i = 0; i < this.devices.length; i++) {
            if (queue.deviceId == this.devices.models[i].get('deviceId')) {
                device = this.devices.models[i];
                break;
            }
        }
        return device;
    },

    addMessageTypeToQueue: function(queue) {
        if (queue.messageType === 'workouts') {
            queue.displayMessageType = Localizer.localize('queue.workout');
            queue.connectUrl = NavigationUtil.url(
                '/workout/' + queue.metaData.messageUrl.substring(queue.metaData.messageUrl.lastIndexOf('/') + 1)
            );
        } else if (queue.messageType === 'activities') {
            queue.displayMessageType = Localizer.localize('queue.activity');
            queue.connectUrl = NavigationUtil.url(
                '/activity/' + queue.messageUrl.substring(queue.messageUrl.lastIndexOf('/') + 1)
            );
        } else if (queue.messageType === 'courses') {
            if (queue.messageUrl.indexOf('course-service/course/copyFromActivity') > -1) {
                queue.displayMessageType = Localizer.localize('queue.activity');
                queue.connectUrl = NavigationUtil.url(
                    '/activity/' +
                        queue.messageUrl.substring(
                            queue.messageUrl.indexOf('fit/') + 4,
                            queue.messageUrl.lastIndexOf('/')
                        )
                );
            } else {
                queue.displayMessageType = Localizer.localize('queue.courses');
                queue.connectUrl = NavigationUtil.url(
                    '/course/' +
                        queue.messageUrl.substring(
                            queue.messageUrl.indexOf('fit/') + 4,
                            queue.messageUrl.lastIndexOf('/')
                        )
                );
            }
        } else if (queue.messageType === 'personal-records') {
            queue.displayMessageType = Localizer.localize('queue.pr');
            queue.messageName = Localizer.localize('queue.pr');
            queue.connectUrl = NavigationUtil.url('/personal-records');
        } else if (queue.messageType === 'schedules') {
            // workouts from calendar
            queue.displayMessageType = Localizer.localize('queue.workout');
            queue.connectUrl = NavigationUtil.url('/calendar');
            if (queue.messageName == 'Schedule') {
                queue.messageName = Localizer.localize('queue_training_schedule');
            } else if (queue.messageName == 'Configuration') {
                queue.messageName = Localizer.localize('queue.configuration');
            }
        } else if (queue.messageType === 'settings' || queue.messageType === 'user-profile') {
            queue.displayMessageType = Localizer.localize('queue.settings');
            queue.connectUrl = NavigationUtil.url('/settings/userSettings');
            if (queue.messageType === 'settings') {
                queue.messageName = Localizer.localize('queue.personal.information');
            } else if (queue.messageType === 'user-profile') {
                queue.messageName = Localizer.localize('queue.personalInf.training.zones');
            }
        } else if (queue.messageType === 'goals') {
            queue.displayMessageType = Localizer.localize('queue.goals');
            queue.messageName = Localizer.localize('queue.goals');
            queue.connectUrl = NavigationUtil.url('/goals');
        } else if (queue.messageType === 'software-update') {
            if (queue.metaData.dataType.indexOf('Chipset') > -1) {
                queue.messageName = queue.metaData.productName;
            } else if (queue.metaData.dataType === 'Other' || queue.metaData.dataType === 'Firmware') {
                queue.messageName = Localizer.localize(
                    'fileType.version',
                    queue.metaData.productName,
                    queue.metaData.version
                );
            } else if (queue.metaData.dataType === 'Text') {
                var pieces = queue.metaData.productName.split(',');
                if (pieces.length == 3) {
                    queue.messageName = pieces[0] + ', ' + pieces[2];
                } else {
                    queue.messageName = queue.metaData.productName;
                }
            } else {
                console.log('Unexpected dataType ' + queue.metaData.dataType);
                queue.messageName = Localizer.localize(
                    'fileType.version',
                    queue.metaData.productName,
                    queue.metaData.version
                );
            }
            queue.displayMessageType = '';
            queue.connectUrl = '#';
        } else if (queue.messageType === 'language-settings') {
            queue.displayMessageType = Localizer.localize('queue.device.settings');
            queue.connectUrl = NavigationUtil.url('/settings/device');
        } else if (queue.messageType === 'sports') {
            queue.connectUrl = NavigationUtil.url('/device/' + queue.deviceId + '?zones=true');
            queue.displayMessageType = Localizer.localize('queue.settings');
            if (queue.messageName === 'Power Curve') {
                queue.connectUrl = NavigationUtil.url(
                    '/report/' + ReportTypeMetrics.ACTIVITY_POWER_CURVE_CYCLING.id + '/cycling/last_four_weeks'
                );
                queue.messageName = Localizer.localize('power_curve_title');
                queue.displayMessageType = Localizer.localize('reports_title');
            } else if (queue.messageUrl.toLowerCase().indexOf('/powerzones') > -1) {
                queue.messageName = Localizer.localize('power.zones');
            } else if (queue.messageUrl.toLowerCase().indexOf('/default') > -1) {
                queue.messageName = Localizer.localize('queue.hrz.other');
            } else if (queue.messageUrl.toLowerCase().indexOf('/cycling') > -1) {
                if (queue.messageUrl.toLowerCase().indexOf('?subsport-type=generic') > -1) {
                    queue.messageName = Localizer.localize('queue.options.bike');
                } else {
                    queue.messageName = Localizer.localize('queue.hrz.cycling');
                }
            } else if (queue.messageUrl.toLowerCase().indexOf('/swimming') > -1) {
                queue.messageName = Localizer.localize('queue.hrz.swimming');
            } else if (queue.messageUrl.toLowerCase().indexOf('/walking') > -1) {
                queue.messageName = Localizer.localize('queue.options.walk');
            } else if (queue.messageUrl.toLowerCase().indexOf('/cardio') > -1) {
                queue.messageName = Localizer.localize('queue.options.cardio');
            } else if (queue.messageUrl.toLowerCase().indexOf('/running') > -1) {
                if (queue.messageUrl.indexOf('device-service') != -1) {
                    if (queue.applicationKey.indexOf('edge') != -1) {
                        queue.messageName = Localizer.localize('device.settings.options.Cycling');
                    } else {
                        if (queue.messageUrl.toLowerCase().indexOf('?subsport-type=treadmill') > -1) {
                            queue.messageName = Localizer.localize('queue.options.runIndoor');
                        } else {
                            if (
                                queue.applicationKey.indexOf('forerunner35') === 0 ||
                                queue.applicationKey.indexOf('foreathlete35') === 0
                            ) {
                                queue.messageName = Localizer.localize('queue.options.runOutdoor');
                            } else {
                                queue.messageName = Localizer.localize('queue.options.running');
                            }
                        }
                    }
                    queue.connectUrl = NavigationUtil.url('/device/' + queue.deviceId);
                } else {
                    queue.messageName = Localizer.localize('queue.hrz.running');
                }
            }
        } else if (queue.messageType === 'device-settings' && queue.messageName === 'auto-sync') {
            queue.messageName = Localizer.localize('queue.auto.sync');
            queue.displayMessageType = Localizer.localize('queue.device.settings');
            queue.connectUrl = '#';
        } else if (queue.messageType === 'device-settings') {
            queue.displayMessageType = Localizer.localize('queue.device.settings');
            queue.connectUrl = NavigationUtil.url('/device/' + queue.deviceId);
        } else if (queue.messageType === 'segment-leaders' || queue.messageType === 'segment-leaders-running') {
            if (queue.isGroupHeading) {
                queue.messageName = Localizer.localize('queue.segments');
            } else {
                if (queue.groupName && queue.groupName.toLowerCase().indexOf('strava-') > -1) {
                    queue.displayMessageType = null;
                    queue.connectUrl = '#';
                } else {
                    queue.displayMessageType = Localizer.localize('queue.segment');
                }
                if (queue.messageUrl.indexOf('strava') > -1) {
                    queue.connectUrl = '#';
                } else if (queue.connectUrl !== '#') {
                    queue.connectUrl = NavigationUtil.url(
                        '/segment/' + queue.messageUrl.substring(queue.messageUrl.lastIndexOf('/') + 1)
                    );
                }
            }
        } else if (queue.messageType === 'golf_club') {
            queue.messageName = Localizer.localize('queue.golf.club');
            queue.displayMessageType = Localizer.localize('queue.golf.club');
            queue.connectUrl = '#';
        } else if (queue.messageType === 'install_app') {
            queue.displayMessageType = this.updateMessageType(
                queue.metaData.appDetails.type,
                queue.metaData.appDetails.developerName
            );
            queue.connectUrl = DeviceLinksUtil.getAppUrl(queue);
        } else if (queue.messageType === 'pace-band') {
            queue.connectUrl = NavigationUtil.url(`/pacepro/edit/${queue.metaData.metaDataId}`);
        } else if (queue.messageType === 'sporting-event') {
            queue.connectUrl = NavigationUtil.url(`/calendar`);
        } else {
            // default values
            console.log('Unknown message type: ' + queue.messageType);
            queue.displayMessageType = '';
            queue.connectUrl = '#';
        }
    },

    updateMessageType: function(appDetailsType, developerName) {
        // 4 file types will be available in queue: Watch faces, data fields, widgets, and activities (will not be called activities, final name TBA)
        var label = '';
        switch (appDetailsType) {
            case 'watchface':
                label = Localizer.localize('queue.venu.faces', developerName);
                break;
            case 'datafield':
                label = Localizer.localize('queue.venu.data.fields', developerName);
                break;
            case 'widget':
                label = Localizer.localize('queue.venu.widgets', developerName);
                break;
            case 'application':
                label = Localizer.localize('queue.venu.apps', developerName);
                break;
        }

        if (developerName === null || jQuery.trim(developerName).length === 0) {
            // Need to remove the developer name place holder since there is no name.
            var label2 = label;
            var startIndex = label.indexOf('(');
            var endIndex = label.indexOf(')');

            label = label.substring(0, startIndex);
            label2 = label2.substring(endIndex + 1);

            label = label + label2;
        }
        return label;
    },

    showErrorMessage: function() {
        if (this.queuePoller) {
            this.queuePoller.stopPolling();
        }

        this.$('#queueNotification-error').show();
    },

    hideErrorMessage: function() {
        this.$('#queueNotification-error').hide();
    },

    closeLists: function() {
        this.$('.sync-list').hide();
    },

    handleQueueCount: function(model) {
        var count = model.get('count');
        this.trigger(Events.REFRESH_COUNT, count);
    }
});

DevicesPopupMenuView.Events = Events;
export default DevicesPopupMenuView;
